import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import ProjectForm from '#component/project/form';
import {
  isoDateOnlyGen,
  mapDevicesToSelect,
  getInitialModules,
  customFieldsToInitValues,
  formatAlertDelayIntoFullTime
} from '#component/project/form/helper';
import { updateBreadcrumb } from '#action/breadcrumb';
import { putProject, fetchProjectsUsersDevices } from '#action/project/dashboard';
import { CLEAR_PROJECT_DATA } from '#action/types';
import { fetchProjectAlerts, putProjectAlerts } from '#action/project/alerts';
import { fetchProjectProducts, putProjectProducts } from '#action/project/products';
import { fetchProjectPackages, putProjectPackages } from '#action/project/packages';
import { fetchProjectCustomFields, putProjectCustomFields, clearProjectCustomFields } from '#action/project/custom-fields';
import {
  fetchProjectAlertConfirmations,
  putProjectAlertConfirmations,
  clearProjectAlertConfirmations
} from '#action/project/alert-confirmations';
import useMinMaxDate from '#component/project/hooks/useMinMaxDate';
import { StyledPeloaderComponent } from './styles';
import propTypes from './prop-types';
import {
  listToTree,
  setDocumentTitle,
  sortDevices,
  sortUsers,
  isUndefinedOrNull,
  getMillisecondsFromTimeDelta
} from '#helper';

const EditProjectForm = props => {
  const [loading, setLoading] = useState(true);
  const { history, match: { params: { id } } } = props;
  const dispatch = useDispatch();
  const account = useSelector(s => s.account, shallowEqual);
  const {
    devices: unsortedDevices = [],
    users,
    projects: unsortedProjects = []
  } = useSelector(s => s.projectsUsersDevices, shallowEqual);
  const projectsAlerts = useSelector(s => s.projectsAlerts, shallowEqual);
  const projectsProducts = useSelector(s => s.projectsProducts, shallowEqual);
  const projectsPackages = useSelector(s => s.projectsPackages, shallowEqual);
  const { customFields } = useSelector(s => s.projectCustomFields, shallowEqual);
  const projectsAlertConfirmations = useSelector(s => s.projectAlertConfirmations, shallowEqual);

  const devices = useMemo(() => sortDevices(unsortedDevices), [unsortedDevices]);

  const projectsTree = useMemo(
    () =>
      listToTree(unsortedProjects.sort((a, b) => a.id - b.id), 0),
    [unsortedProjects]
  );

  const currentProject = useMemo(() =>
    unsortedProjects.find(p => p.id === Number(id)), [unsortedProjects, id]);

  const isRootProject = Boolean(
    unsortedProjects.find(p => p.level === 0 && p.id === currentProject?.id)
  );

  let parentProject = useMemo(() => unsortedProjects.find(
    p => p.id === currentProject?.parentId
  ), [currentProject, unsortedProjects]);

  if (currentProject && !parentProject) {
    parentProject = {
      beginDate: currentProject.beginDate,
      expirationDate: currentProject.expirationDate,
      disableFieldsInRoot: typeof currentProject.parentId !== 'undefined'
    };
  }

  const defaultData = useMemo(() =>
    mapDevicesToSelect(devices), [devices]);

  const selectedUsers = useMemo(() => sortUsers(users), [users]);

  const defaultUser = useMemo(() => selectedUsers.map(u => ({
    label: `${u.firstName} ${u.lastName}`,
    value: u.id,
    disabled: u.disabled
  })), [selectedUsers]);

  const minMaxDate = useMinMaxDate(parentProject);

  const initialValues = useMemo(() => {
    if (currentProject && parentProject) {
      const projectAlerts = projectsAlerts[id] ?? [];

      const iV = {
        parentId: currentProject.parentId,
        title: currentProject.title,
        beginDate: currentProject.beginDate,
        isExpirationDate: currentProject.expirationDate ? '1' : '0',
        devices: defaultData,
        users: defaultUser,
        description: currentProject.description ?? '',
        disableIsExpirationDate:
          parentProject.disableFieldsInRoot || Boolean(parentProject.expirationDate),
        alerts: projectAlerts,
        alertConfirmations: projectsAlertConfirmations[id] ?? [],
        products: projectsProducts[id] ?? [],
        packages: projectsPackages[id] ?? [],
        customFields: customFieldsToInitValues(customFields),
        expirationDate: currentProject.expirationDate ?
          isoDateOnlyGen(new Date(currentProject.expirationDate)) : null,
        disableFieldsInRoot: parentProject.disableFieldsInRoot,
        modules: getInitialModules(currentProject, parentProject, isRootProject) ?? []
      };

      if (projectAlerts?.length) {
        iV.alerts = formatAlertDelayIntoFullTime(projectAlerts);
      }

      return iV;
    }

    return {};
  }, [
    currentProject,
    defaultData,
    parentProject,
    projectsAlerts,
    projectsProducts,
    projectsPackages,
    customFields,
    projectsAlertConfirmations,
    id,
    defaultUser,
    isRootProject
  ]);

  useEffect(() => {
    setDocumentTitle('Edit Project', currentProject?.title);
    dispatch(
      updateBreadcrumb([
        { label: 'Projects', path: '/project' },
        { label: 'Edit project' }
      ])
    );
  }, [currentProject?.title, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(fetchProjectsUsersDevices(id)),
        dispatch(fetchProjectAlerts(id)),
        dispatch(fetchProjectProducts(id)),
        dispatch(fetchProjectPackages(id)),
        dispatch(fetchProjectCustomFields(id)),
        dispatch(fetchProjectAlertConfirmations(id))
      ]);
      setLoading(false);
    };

    fetchData();

    return () => {
      dispatch({ type: CLEAR_PROJECT_DATA });
      dispatch(clearProjectCustomFields());
      dispatch(clearProjectAlertConfirmations());
    };
  }, [dispatch, id]);

  const handleFormSubmit = async values => {
    const {
      title,
      expirationDate,
      beginDate,
      products,
      packages,
      description,
      modules,
      customFields: projectCustomFields,
      alertConfirmations
    } = values;

    const data = {
      title,
      beginDate: beginDate.slice(0, 10),
      users: values.users.map(u => u.value),
      modules
    };

    if (values.devices) {
      data.devices = values.devices.map(d => d.id);
    }

    if (expirationDate) {
      data.expirationDate = expirationDate.slice(0, 10);
    }

    if (description) {
      data.description = description;
    }

    let alerts = null;

    if (values.alerts?.length) {
      const keys = ['tMin', 'tMax', 'hMin', 'hMax', 'bMin', 'startDelay', 'minAlertPeriod'];
      alerts = values.alerts.map(alert => {
        const transformedAlert = { label: alert.label };
        if (typeof alert.id === 'number') transformedAlert.id = alert.id;
        Object.keys(alert).forEach(key => {
          if (keys.includes(key) && !isUndefinedOrNull(alert[key]) && alert[key] !== '') {
            if (key === 'startDelay' || key === 'minAlertPeriod') {
              // Convert startDelay to milliseconds
              transformedAlert[key] = getMillisecondsFromTimeDelta(alert[key]);
            } else {
              transformedAlert[key] = Number((+alert[key]).toFixed(1));
            }
          }
        });
        return transformedAlert;
      });
    }

    let alertConfirmationsForFetch = null;
    if (alertConfirmations) {
      alertConfirmationsForFetch = alertConfirmations.map(alertConfirmation => {
        if (typeof alertConfirmation.id === 'string') delete alertConfirmation.id;

        return alertConfirmation;
      });
    }

    let productsForFetch = null;

    if (products?.length) {
      productsForFetch = products.map(product => {
        const valideProduct = { ...product };
        if (typeof valideProduct.id === 'string') delete valideProduct.id;

        return valideProduct;
      });
    }

    let packagesForFetch = null;

    if (packages?.length) {
      packagesForFetch = packages.map(pack => {
        const validePackage = { ...pack };
        if (typeof validePackage.id === 'string') delete validePackage.id;

        return validePackage;
      });
    }

    await dispatch(putProject(id, data));
    const [alr, arlCnf, prd, pck, pcf] = await Promise.all([
      dispatch(putProjectAlerts(id, alerts ?? [])),
      dispatch(putProjectAlertConfirmations(id, alertConfirmationsForFetch ?? [])),
      dispatch(putProjectProducts(id, productsForFetch ?? [])),
      dispatch(putProjectPackages(id, packagesForFetch ?? [])),
      dispatch(putProjectCustomFields(id, projectCustomFields ?? []))
    ]);
    if (alr.success && arlCnf.success && prd.success && pck.success && pcf.success) {
      history.push('/project');
    }
  };

  if (loading) {
    return <StyledPeloaderComponent />;
  }

  if (!currentProject) {
    return null;
  }

  return (
    <ProjectForm
      title="Edit project"
      {...props}
      initialValues={initialValues}
      arrayProjects={unsortedProjects}
      projects={projectsTree}
      devices={devices}
      users={users}
      minMaxDate={minMaxDate}
      editProject
      currentProject={currentProject}
      handleFormSubmit={handleFormSubmit}
      editable={false}
      account={account}
    />
  );
};

EditProjectForm.propTypes = propTypes;

export default EditProjectForm;
